import type { InitiativeDetailResponse, InitiativeWriteInput } from '@complan/contracts';

/**
 * Maps the Detail_Panel read (`GET /api/initiatives/:id`) into the
 * {@link InitiativeWriteInput} shape the Initiative_Intake_Form wizard edits and
 * `PUT /api/initiatives/:id` accepts — the seam the edit route needs to open the
 * wizard pre-filled with the record's current values.
 *
 * Most fields carry across unchanged; three need translation because the detail
 * projection and the write payload disagree on shape or on what is even stored:
 *
 * - **Milestones, dependencies, Field Force scope, reference links** each drop
 *   the row `id` the detail read adds (R12.2-R12.4, R12.9): the write schema has
 *   no id field for any of them — `updateInitiative` replaces the whole
 *   collection on every save (delete-then-insert), so the stored id never
 *   round-trips through an edit.
 * - **`salesCycleAssignment`** is read back as one flat object carrying both the
 *   manually assigned set and the currently derived set (R7.6); the write shape
 *   is a discriminated union with only the *effective* set. Editing preserves the
 *   currently effective assignment: a manual override's `assignedSalesCycleIds`
 *   and its stored reason, or the derived set with no reason.
 * - **`confidentialityReason`** has no home on the detail projection —
 *   `initiative-write-repository.ts` notes it belongs to the Audit_Log, written
 *   only when a confidentiality *change* is detected (R3.10) — so it always
 *   starts `null` here. A user changing the confidentiality attribute on this
 *   edit supplies a fresh reason through the Governance step exactly as they
 *   would on create; leaving it unchanged sends `null`, which the server reads
 *   as "no confidentiality change on this save".
 */
export function mapDetailToWriteInput(detail: InitiativeDetailResponse): InitiativeWriteInput {
  const assignment = detail.salesCycleAssignment;

  return {
    title: detail.title,
    details: detail.details,
    ownerUserId: detail.ownerUserId,
    backupOwnerUserId: detail.backupOwnerUserId,
    startDate: detail.startDate,
    endDate: detail.endDate,
    platforms: detail.platforms,
    channelIds: [...detail.channelIds],
    customerGroupIds: [...detail.customerGroupIds],
    audienceIds: [...detail.audienceIds],
    categoryIds: [...detail.categoryIds],
    initiativeTypeIds: [...detail.initiativeTypeIds],
    cscAttributeIds: [...detail.cscAttributeIds],
    salesCycleAssignment:
      assignment.source === 'manual'
        ? {
            source: 'manual',
            salesCycleIds: [...assignment.assignedSalesCycleIds],
            // Always a 10-500 character string once `source` is `manual` — the
            // server never stores a manual override without one (R7.4).
            reason: assignment.overrideReason ?? '',
          }
        : { source: 'derived', salesCycleIds: [...assignment.assignedSalesCycleIds] },
    priorityId: detail.priorityId,
    isConfidential: detail.isConfidential,
    confidentialityReason: null,
    fieldForceInvolvement: detail.fieldForceInvolvement,
    dteInvolvement: detail.dteInvolvement,
    cscInvolvement: detail.cscInvolvement,
    milestones: detail.milestones.map((milestone) => ({
      milestoneTypeId: milestone.milestoneTypeId,
      plannedDate: milestone.plannedDate,
      completedDate: milestone.completedDate,
    })),
    dependencies: detail.dependencies.map((dependency) => ({
      dependencyFunction: dependency.dependencyFunction,
      description: dependency.description,
      ownerText: dependency.ownerText,
      dueDate: dependency.dueDate,
      dueEndDate: dependency.dueEndDate,
      completedAt: dependency.completedAt,
    })),
    fieldForceScope: detail.fieldForceScope.map((scope) => ({
      fieldForceRole: scope.fieldForceRole,
      activityTypeId: scope.activityTypeId,
      audienceId: scope.audienceId,
      hasMaterial: scope.hasMaterial,
      workOrder: scope.workOrder,
      informationContribution: scope.informationContribution,
      // `complan-manpower` Requirement 4's demand inputs, carried across for the
      // same reason every other stored field is: `PUT /api/initiatives/:id` is a
      // full replace, so a field this mapper drops is a field the next save
      // erases. `isPopulationOverride`, `derivedPopulationCount` and
      // `populationSourceTimestamp` are deliberately *not* carried - they are
      // read-only projections the server re-derives from the selection and the
      // count on every write and read, and the write schema has no field for any
      // of them.
      populationUnitId: scope.populationUnitId,
      regionId: scope.regionId,
      posSegmentIds: [...scope.posSegmentIds],
      impactedPopulationCount: scope.impactedPopulationCount,
      populationOverrideReason: scope.populationOverrideReason,
      coverageFactor: scope.coverageFactor,
      frequency: scope.frequency,
      activityStartDate: scope.activityStartDate,
      activityEndDate: scope.activityEndDate,
    })),
    referenceLinks: detail.referenceLinks.map((link) => ({
      label: link.label,
      url: link.url,
    })),
  };
}
